import {
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import type { ReactNode } from "react";

export interface ReportSectionHeaderProps {
  onBack: () => void;
  title: string;
  backLabel: string;
  /** Small label above the title (e.g. parent “Reports” area). */
  contextLabel?: string;
  /** Optional icon or illustration to the left of the title block. */
  leadingVisual?: ReactNode;
}

/**
 * Shared chrome for in-panel report views: clear back action, hierarchy, and spacing.
 * Used by site meeting, annual summary, printout search, and any future report sections.
 */
export function ReportSectionHeader({
  onBack,
  title,
  backLabel,
  contextLabel,
  leadingVisual,
}: Readonly<ReportSectionHeaderProps>) {
  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      mb="lg"
      shadow="xs"
      bg="var(--mantine-color-gray-0)"
    >
      <Stack gap="md">
        <Button
          variant="light"
          color="gray"
          size="sm"
          radius="md"
          leftSection={<IconArrowLeft size={18} stroke={1.5} />}
          onClick={onBack}
          styles={{
            root: {
              alignSelf: "flex-start",
              fontWeight: 600,
              border: "1px solid var(--mantine-color-gray-3)",
            },
          }}
        >
          {backLabel}
        </Button>
        <Group align="flex-start" gap="md" wrap="nowrap">
          {leadingVisual}
          <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
            {contextLabel ? (
              <Text
                size="xs"
                fw={700}
                c="dimmed"
                tt="uppercase"
                style={{ letterSpacing: "0.06em" }}
              >
                {contextLabel}
              </Text>
            ) : null}
            <Title order={3} fw={700} lh={1.25} m={0}>
              {title}
            </Title>
          </Stack>
        </Group>
      </Stack>
    </Paper>
  );
}
